import { DateTime } from 'luxon';

import { dateStringErrorMessage } from '@opengov/reporting-formatters/formatters/calendarUnitFormatters';
import { CalendarUnit } from '@opengov/reporting-types';

// This file to be replaced w/ version in reporting-formatters
// Formatting code should be moved to FE to enable localization

const prettyDate = (dateString: string, format: string): string => {
  return DateTime.fromISO(dateString, { zone: 'utc' }).toFormat(format);
};
const fiscalMonths = [1, 1, 1, 2, 2, 2, 3, 3, 3, 4, 4, 4];

export const toFiscalYear = (dateString: string): string => {
  return prettyDate(dateString, `'FY'yyyy`);
};

export const toFiscalPeriod = (periodString: string): string => {
  if (!periodString) {
    return '';
  }
  const yearAndPeriod = periodString.split(' ');
  const year = yearAndPeriod[0];
  const period = Number(yearAndPeriod[1]);
  return `Period ${period} FY${year}`;
};

export const toFiscalQuarterAndYear = (dateString: string, fiscalYearStartMonth: number): string => {
  // DateTime.fromISO('2020-01-01T00:00:00.000Z').month returns 12 in PDX!
  // The dates coming off the queries can have the T..Z which are in UTC and have already been
  // translated to a FY start of Jan
  const dateTime = DateTime.fromISO(dateString, { zone: 'utc' }).toUTC();
  const month = dateTime.month;
  const paddedOffset = `${fiscalYearStartMonth}`.padStart(2, '0');
  const fiscalMonth = month < fiscalYearStartMonth ? 12 - fiscalYearStartMonth + month : month - fiscalYearStartMonth;
  // remember that arrays are zero based. fiscalMonth === 0 for January, etc.
  const quarter = fiscalMonths[fiscalMonth];

  const fiscalYear =
    month >= fiscalYearStartMonth
      ? DateTime.fromISO(`${dateTime.year}-${paddedOffset}-01`).plus({ years: 1 }).minus({ days: 1 }).year
      : dateTime.year;

  return `Q${quarter} FY${fiscalYear}`;
};

// This needs to be replaced w/ version in reporting-formatters
// Formatting code should be moved to FE to enable localization
export const calendarUnitFormatters = {
  [CalendarUnit.Day]: {
    formatter: () => (dateString) => dateStringErrorMessage(dateString) || prettyDate(dateString, 'MM/dd/yyyy')
  },
  [CalendarUnit.Week]: {
    formatter: () => (dateString) => dateStringErrorMessage(dateString) || prettyDate(dateString, 'MM/dd/yyyy')
  },
  [CalendarUnit.Month]: {
    formatter: () => (dateString) => dateStringErrorMessage(dateString) || prettyDate(dateString, 'MMMM yyyy')
  },
  [CalendarUnit.Quarter]: {
    formatter: () => (dateString) => dateStringErrorMessage(dateString) || prettyDate(dateString, `'Q'q yyyy`)
  },
  [CalendarUnit.FiscalQuarter]: {
    formatter: (params: { entityFiscalYearStartMonth: number }) => (dateString) => {
      const entityFiscalYearStartMonth = params && params.entityFiscalYearStartMonth;
      return dateStringErrorMessage(dateString) || toFiscalQuarterAndYear(dateString, entityFiscalYearStartMonth);
    }
  },
  [CalendarUnit.Year]: {
    formatter: () => (dateString) => dateStringErrorMessage(dateString) || prettyDate(dateString, 'yyyy')
  },
  [CalendarUnit.FiscalYear]: {
    formatter: () => (dateString) => dateStringErrorMessage(dateString) || toFiscalYear(`${parseInt(dateString, 10)}`)
  },
  [CalendarUnit.FiscalPeriod]: {
    // A period/year combo can't effectively be conveyed with an ISO date.
    // Instead, the organize-by query for fiscal period/year returns a string
    // like "2018 13" for Period 13, FY 2017-18.
    formatter: () => (periodString) => dateStringErrorMessage(periodString) || toFiscalPeriod(periodString)
  }
};
